import React from 'react';
import strings from 'strings';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';

const {
  bulkEmails: {
    dialogs: {
      cancel: {
        copies: { title, message, saveButton, keepEditingButtonText, discardButtonText },
        variants: dialogCopiesVariants
      }
    }
  }
} = strings;

/**
 * @typedef {Object} DiscardBulkDialogProps
 * @property {boolean} [isBulkBeingEdited=false] - Indicates if the bulk process is currently in an edit state to determine string references.
 * @property {Function} onDiscard - Callback executed when the user confirms they want to discard changes.
 * @property {Function} [onKeepEditing] - Callback executed when the user closes the dialog or chooses to continue editing.
 * @property {Function} [onSaveChanges] - Callback executed when the "Save Changes" button is clicked.
 * @property {string} selectedMenuItem - The key used to look up specific string references in the `refs` configuration.
 */

/**
 * A warning dialog that intercepts user navigation to prevent accidental loss of data
 * during bulk email creation or editing.
 * @param {DiscardBulkDialogProps} props
 * @returns {JSX.Element}
 */
export const DiscardBulkDialog = ({
  isBulkBeingEdited = false,
  onDiscard,
  onKeepEditing,
  onSaveChanges,
  selectedMenuItem
}) => {
  const refsByBulkType =
    (isBulkBeingEdited && dialogCopiesVariants[selectedMenuItem]) || dialogCopiesVariants.default;

  const handleCloseDialog = () => onKeepEditing && onKeepEditing();
  const handleDiscardChanges = ok => {
    if (ok) {
      onDiscard();
    }
  };

  const CustomDialogActions = ({ defaultButtonsProps, ActionsMapper }) => {
    const { onClose: closeDialog, onConfirm: discardChanges } = defaultButtonsProps;

    const buttons = [
      {
        variant: 'outlined',
        text: discardButtonText,
        onClick: discardChanges,
        show: true
      },
      {
        variant: 'outlined',
        text: strings.formatString(saveButton, refsByBulkType),
        onClick: onSaveChanges,
        show: !!onSaveChanges
      },
      {
        variant: 'contained',
        text: keepEditingButtonText,
        onClick: closeDialog,
        show: true
      }
    ];

    return <ActionsMapper buttons={buttons} />;
  };

  return (
    <DecisionDialog
      CustomActions={CustomDialogActions}
      isOpened
      message={strings.formatString(message, refsByBulkType)}
      onClose={handleCloseDialog}
      onConfirm={handleDiscardChanges}
      severity="warning"
      showSeverityIcon
      title={strings.formatString(title, refsByBulkType)}
      withButtons="YesNo"
    />
  );
};
